import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #preload = null;

  /** @type {MutationObserver | null} */
  #contentsObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#schedulePreload();
    this.#watchContents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#preload?.abort();
    this.#contentsObserver?.disconnect();
  }

  /**
   * A cart change redraws only the drawer's contents (sections/cart-drawer.liquid), not the dialog around them.
   * The dialog's empty-cart class and its label follow the contents here, as the header's markup sets them
   * when the page is drawn.
   */
  #watchContents() {
    const dialog = /** @type {Refs} */ (this.refs).dialog;
    const contents = dialog?.querySelector('.cart-drawer__inner');
    if (!dialog || !contents) return;

    const sync = () => {
      const empty = !contents.querySelector('#cart-drawer-heading');
      if (dialog.classList.contains('cart-drawer--empty') === empty) return;
      dialog.classList.toggle('cart-drawer--empty', empty);
      dialog.setAttribute('aria-labelledby', empty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
    };

    this.#contentsObserver?.disconnect();
    this.#contentsObserver = new MutationObserver(sync);
    this.#contentsObserver.observe(contents, { childList: true, subtree: true });
  }

  /**
   * A cart that already holds something has its recommendations fetched once the page has loaded and the browser
   * is idle, so the cards and their pictures are there when the drawer opens. An empty cart renders no
   * recommendations element and costs nothing here.
   */
  #schedulePreload() {
    this.#preload?.abort();
    const controller = new AbortController();
    this.#preload = controller;
    const run = () => {
      if (controller.signal.aborted) return;
      const dialog = /** @type {Refs} */ (this.refs).dialog;
      if (dialog?.querySelector('cart-recommendations-component')) this.#loadRecommendations(dialog);
    };
    const whenIdle = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 4000 });
      else setTimeout(run, 2000);
    };
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true, signal: controller.signal });
  }

  /** @param {HTMLDialogElement} dialog */
  #loadRecommendations(dialog) {
    if (customElements.get('cart-recommendations-component')) return;
    import('@theme/cart-recommendations').catch(() => {
      const recommendations = dialog.querySelector('cart-recommendations-component');
      if (recommendations) recommendations.hidden = true;
    });
  }

  /** @param {CustomEvent} event */
  #handleCartAdd = (event) => {
    // A refused add (sold out, quantity limit) shows its reason beside the button; the drawer would cover it.
    if (event?.detail?.data?.didError) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // A cart filled after the page loaded was not preloaded: load the recommendations module on opening.
    this.#loadRecommendations(dialog);

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
